<template>
  <v-dialog
    class="user-op-log-dialog"
    :width="dialogWidth"
    title="Operation History"
    :visible.sync="dialogVisible"
    :z-index="2000"
    :scrollbar="false"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div class="op-log-content">
      <div class="table-wrap">
        <el-table
          :data="tableData"
          v-loading="tableLoading"
          element-loading-text="Loading..."
          ref="table"
          :row-key="rowKeyHandler"
          size="mini"
          border
        >
          <el-table-column type="expand" width="40">
            <template slot-scope="{ row }">
              <div class="field-changes-wrap">
                <el-table
                  :data="row.fieldChanges"
                  border
                  size="mini"
                  :show-header="true"
                >
                  <el-table-column
                    prop="fieldName"
                    label="Field Name"
                    min-width="100"
                  />
                  <el-table-column
                    prop="oldValue"
                    label="Old Value"
                    min-width="120"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    prop="newValue"
                    label="New Value"
                    min-width="120"
                    show-overflow-tooltip
                  />
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Update Type" min-width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag
                :type="updateTypeTagType(row.updateType)"
                effect="dark"
                size="mini"
              >
                {{ row.updateType }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Update Time" min-width="140" align="center">
            <template slot-scope="{ row }">
              {{ formatTime(row.updateTime) }}
            </template>
          </el-table-column>
          <el-table-column
            prop="operatorName"
            label="Operator Name"
            min-width="100"
            show-overflow-tooltip
          />
        </el-table>
      </div>
      <div class="pagination-wrap">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 25, 50, 100]"
          :page-size="pageSize"
          :layout="paginationLayout"
          :total="total"
          small
        />
      </div>
      <div class="actions">
        <el-button @click="dialogVisible = false">Close</el-button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "UserOperationLogDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
      required: true,
    },
    data: Object,
  },
  data() {
    return {
      dialogVisible: this.visible,
      tableLoading: true,
      tableData: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      userId: "",
    };
  },
  watch: {
    visible(value) {
      this.dialogVisible = value;
    },
  },
  computed: {
    dialogWidth() {
      return "min(980px, 96vw)";
    },
    paginationLayout() {
      const w = window.innerWidth || document.documentElement.clientWidth || 960;
      return w < 900
        ? "total, prev, pager, next"
        : "total, sizes, prev, pager, next, jumper";
    },
  },
  methods: {
    rowKeyHandler(row) {
      return row.operationId;
    },
    updateTypeTagType(updateType) {
      switch (updateType) {
        case "CREATE":
          return "success";
        case "UPDATE":
          return "warning";
        case "DELETE":
          return "danger";
        case "TRANSFER":
          return "primary";
        default:
          return "info";
      }
    },
    formatTime(time) {
      if (!time) return "";
      // 后端已返回格式化文本时直接展示，避免 Date 解析引入时差
      if (typeof time === "string" && !time.includes("T")) return time;
      return this.moment(new Date(time)).format("YYYY-MM-DD HH:mm:ss");
    },
    onOpened() {
      if (this.data && this.data.UserID) {
        this.userId = this.data.UserID;
        this.currentPage = 1;
        this.getTableData();
      }
    },
    onClosed() {
      this.tableData = [];
      this.total = 0;
      this.userId = "";
      this.currentPage = 1;
      this.pageSize = 10;
      this.$emit("update:data", null);
      this.$emit("update:visible", false);
    },
    getTableData() {
      this.tableLoading = true;
      this.axios
        .get("/user/operationLogs", {
          params: {
            userId: this.userId,
            page: this.currentPage,
            pageSize: this.pageSize,
          },
        })
        .then(({ code, data, msg }) => {
          this.tableLoading = false;
          if (code === 200) {
            const payload = data || {};
            this.tableData = payload.list || [];
            this.total = payload.total || 0;
          } else {
            this.$message.error(msg || "Load operation logs failed.");
          }
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },
    handleSizeChange(pageSize) {
      this.pageSize = pageSize;
      this.currentPage = 1;
      this.getTableData();
    },
    handleCurrentChange(currentPage) {
      this.currentPage = currentPage;
      this.getTableData();
    },
  },
};
</script>

<style lang="scss" scoped>
.user-op-log-dialog {
  ::v-deep .el-dialog {
    max-width: 96vw;
    margin-top: 5vh !important;
  }

  ::v-deep .el-dialog__body {
    overflow: hidden;
    padding: 10px 16px 16px;
  }
}

.op-log-content {
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
  width: 100%;

  ::v-deep .el-table {
    width: 100% !important;
  }
}

.field-changes-wrap {
  padding: 8px 8px 8px 24px;
  max-width: 100%;
  overflow-x: auto;
  box-sizing: border-box;

  ::v-deep .el-table {
    width: 100%;
    min-width: 360px;
    table-layout: fixed;
  }
}

.pagination-wrap {
  flex-shrink: 0;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  padding-top: 8px;
  box-sizing: border-box;

  ::v-deep .el-pagination {
    white-space: nowrap;
  }
}

.actions {
  flex-shrink: 0;
  margin-top: 12px;
  text-align: center;
  padding: 4px 0 0;
}
</style>
